export type AuthMode = "charity" | "donor" | "admin";

interface Token {
  token: string;
  expiresAt: number;
}

export interface RequestNewTokenRequest {
  token: string;
}

export interface RequestNewTokenResponse {
  accessToken: Token;
  refreshToken: Token;
}

export interface SignInRequest {
  email: string;
}

export interface AuthorizeRequest {
  mode: "otp";
  input: {
    email: string;
    otp: string;
  };
}

/** Shape returned by POST /auth/login/validate (tokens + user payload) */
export type LoginValidateResponse<TUser> = Omit<
  TUser,
  "accessToken" | "refreshToken"
> & {
  accessToken: { token: string; expiresAt: number };
  refreshToken: { token: string; expiresAt: number };
  firstName?: string;
};

type ApiRequest = <T>(
  route: string,
  request: {
    method: "GET" | "POST" | "PUT" | "DELETE";
    data?: Record<string, unknown>;
    params?: Record<string, string | number>;
  }
) => Promise<T>;

interface CreateAuthServiceOptions<TUser> {
  apiBase: string;
  authMode: AuthMode;
  apiRequest: ApiRequest;
}

export const createAuthService = <TUser>({
  apiBase,
  authMode,
  apiRequest,
}: CreateAuthServiceOptions<TUser>) => {
  const routes = {
    signin: `${apiBase}/auth/login/request`,
    signOut: `${apiBase}/auth/logout`,
    authorize: `${apiBase}/auth/login/validate`,
    token: `${apiBase}/auth/refresh`,
  };

  const requestNewToken = ({ token }: RequestNewTokenRequest) =>
    apiRequest<RequestNewTokenResponse>(routes.token, {
      method: "POST",
      data: { token, authMode, type: authMode },
    });

  const inflightSignIn = new Map<string, Promise<void>>();
  const signIn = ({ email }: SignInRequest) => {
    const key = email.trim().toLowerCase();
    const existing = inflightSignIn.get(key);
    if (existing) return existing;
    const request = apiRequest<void>(routes.signin, {
      method: "POST",
      data: { email, authMode, type: authMode },
    }).finally(() => {
      inflightSignIn.delete(key);
    });
    inflightSignIn.set(key, request);
    return request;
  };

  const signOut = () =>
    apiRequest<void>(routes.signOut, {
      method: "POST",
      data: { authMode, type: authMode },
    });

  const authorize = (input: AuthorizeRequest) =>
    apiRequest<LoginValidateResponse<TUser>>(routes.authorize, {
      method: "POST",
      data: { ...input, authMode, type: authMode },
    });

  return {
    authMode,
    routes,
    requestNewToken,
    signIn,
    signOut,
    authorize,
  };
};
